/**
 * 展示間（雲端簡報室）
 * 動態路由 /ee_ops_centers/:code/present
 *
 * 一直在 Viewer 模式（左原件 + 右對照文字）。
 * 目錄（跳轉選單）是可收合的左側 Drawer，隨時可叫出跳到任一素材。
 *
 * 快捷鍵：
 *   ← →      上下張
 *   Space   下一張
 *   Home    第一張
 *   End     最後一張
 *   F       切換全螢幕
 *   I       切換對照文字面板
 *   T       切換目錄（跳轉選單）
 *   Esc     退出全螢幕
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, IconButton, Chip, Paper,
  LinearProgress, Divider, Drawer, List, ListItemButton, ListItemText,
  Select, MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  NavigateBefore as NavBeforeIcon,
  NavigateNext as NavNextIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  RecordVoiceOver as HostOnIcon,
  VoiceOverOff as HostOffIcon,
  Loop as LoopIcon,
  TrendingFlat as OnceIcon,
} from '@mui/icons-material';

import { useMessage } from '../contexts/MessageContext';
import { getPresentation, getCenterByCode } from '../services/opsCenterService';
import { PresentationResponse, PresentSlide, PresentTag } from '../types/opsCenter';
import { getI18nValue } from '../utils/i18nHelper';
import { logView } from '../utils/userLogHelper';
import { usePermission } from '../hooks/usePermission';
import AuthImage from '../components/AuthImage';

const FUNC_CODE = 'ee_ops_centers';

const TOC_WIDTH = 280;

const OpsCenterPresentPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showApiError } = useMessage();
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();

  const [data, setData] = useState<PresentationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [showToc, setShowToc] = useState(false);

  // 自動播放：每 N 秒換一頁，到末頁回到第一頁循環
  const [autoPlay, setAutoPlay] = useState(false);
  const [intervalSec, setIntervalSec] = useState(5);

  // 主持人朗讀模式（Web Speech API）
  const [hostMode, setHostMode] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  // 播放策略：true = 循環（到末頁回第一頁）；false = 一次（到末頁停止）
  const [loopMode, setLoopMode] = useState(true);

  // ref：speech onend / timer 在 closure 裡要讀最新值，不能用 captured 值
  const autoPlayRef = useRef(autoPlay);
  const loopModeRef = useRef(loopMode);
  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);
  useEffect(() => { loopModeRef.current = loopMode; }, [loopMode]);

  // 分類過濾（'__all__' = 全部；URL 帶 ?category=X 進來時預設套用）
  const [categoryFilter, setCategoryFilter] = useState<string>(
    () => searchParams.get('category') || '__all__'
  );
  // Tag 過濾（多選；URL 帶 ?tag_ids=1,2 進來時預設套用）
  const [tagFilter, setTagFilter] = useState<number[]>(() => {
    const raw = searchParams.get('tag_ids');
    if (!raw) return [];
    return raw.split(',').map((s) => Number(s.trim())).filter((n) => !isNaN(n) && n > 0);
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      if (!code) return;
      setLoading(true);
      const filters = {
        code,
        category: searchParams.get('category') || undefined,
        tag_ids: searchParams.get('tag_ids') || undefined,
      };
      try {
        const c = await getCenterByCode(code);
        const d = await getPresentation(c.id);
        setData(d);
        logView(FUNC_CODE, {
          action: 'enter_presentation',
          ...filters,
          center_id: c.id,
          slide_count: d.slide_count,
        });
      } catch (e: any) {
        logView(FUNC_CODE, { action: 'enter_presentation', ...filters },
                String(e?.message || e));
        showApiError(e);
      }
      finally { setLoading(false); }
    })();
  }, [code, showApiError, searchParams]);

  // 從 slides 抽出所有出現過的 category
  const allCategories = useMemo<string[]>(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.slides.forEach((s) => { if (s.category) set.add(s.category); });
    return Array.from(set);
  }, [data]);

  // 套用過濾（category + tag 取交集）
  const filteredSlides: PresentSlide[] = useMemo(() => {
    if (!data) return [];
    let arr = data.slides;
    if (categoryFilter === '__nocat__') arr = arr.filter((s) => !s.category);
    else if (categoryFilter !== '__all__') arr = arr.filter((s) => s.category === categoryFilter);
    if (tagFilter.length > 0) {
      // AND 邏輯：素材必須掛上所有選定 tag 才符合
      arr = arr.filter((s) => {
        const ids = s.tag_ids ?? [];
        return tagFilter.every((tid) => ids.includes(tid));
      });
    }
    return arr;
  }, [data, categoryFilter, tagFilter]);

  const slide: PresentSlide | undefined = filteredSlides[idx];
  const total = filteredSlides.length;

  // 過濾切換時把 idx 拉回 0，避免跑出範圍
  useEffect(() => {
    setIdx(0);
  }, [categoryFilter, tagFilter]);

  // tag id → tag 物件（顯示用）
  const tagById = useMemo(() => {
    const m = new Map<number, PresentTag>();
    (data?.tags ?? []).forEach((t) => m.set(t.id, t));
    return m;
  }, [data]);

  const jumpTo = useCallback((i: number) => {
    setIdx(Math.max(0, Math.min(i, total - 1)));
    setShowToc(false);
  }, [total]);

  const next = useCallback(() => setIdx((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);
  const first = useCallback(() => setIdx(0), []);
  const last = useCallback(() => setIdx(total - 1), [total]);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) { /* ignore */ } // silent ok: fullscreen toggle browser error, non-blocking
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // 目錄開啟時，← → 讓 Drawer 內焦點優先（不攔截）
      if (showToc && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
      // 下一張：→ / Space / PageDown（presenter clicker 主流發 PageDown）
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault(); next();
      }
      // 上一張：← / PageUp / Backspace（同上，clicker 也常發 PageUp）
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp' || e.key === 'Backspace') {
        e.preventDefault(); prev();
      }
      else if (e.key === 'Home') { e.preventDefault(); first(); }
      else if (e.key === 'End') { e.preventDefault(); last(); }
      else if (e.key === 'f' || e.key === 'F') { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === 'i' || e.key === 'I') { e.preventDefault(); setShowInfo((s) => !s); }
      else if (e.key === 't' || e.key === 'T') { e.preventDefault(); setShowToc((s) => !s); }
      else if (e.key === 'p' || e.key === 'P') { e.preventDefault(); setAutoPlay((p) => !p); }
      else if (e.key === 'm' || e.key === 'M') { e.preventDefault(); setHostMode((h) => !h); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, first, last, toggleFullscreen, showToc]);

  // 自動播放 timer：每 intervalSec 秒前進一頁
  // 循環模式：到末頁回第一頁
  // 一次模式：到末頁停止（自動暫停）
  // 主持人模式開啟時不用 timer，改用 speech onend 事件推進
  useEffect(() => {
    if (!autoPlay || total <= 1) return;
    if (hostMode) return;
    const t = setInterval(() => {
      setIdx((prev) => {
        if (prev >= total - 1) {
          if (loopModeRef.current) return 0;
          // 一次模式：保持在末頁，並停止 autoPlay
          setAutoPlay(false);
          return prev;
        }
        return prev + 1;
      });
    }, Math.max(1, intervalSec) * 1000);
    return () => clearInterval(t);
  }, [autoPlay, intervalSec, total, hostMode]);

  // 主持人朗讀：必須 hostMode + autoPlay 同時 ON 才實際朗讀
  // - 🎤 alone（hostMode ON, autoPlay OFF）= 設定「下次播放要用語音」，不主動觸發
  // - ▶ alone（autoPlay ON, hostMode OFF）= 靜音自動播放（用 fixed timer）
  // - 🎤 + ▶ 都 ON = 念出來、念完才推進下一張
  useEffect(() => {
    if (!hostMode || !autoPlay || !slide || total <= 0) return;
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const text = (
      getI18nValue(slide.ai_summary, i18n.language) || slide.raw_text || ''
    ).slice(0, 1500);  // 防超長 utterance

    if (!text.trim()) {
      // 沒文字可念：靜默推進
      const t = setTimeout(() => {
        if (autoPlayRef.current) setIdx((i) => (i + 1) % total);
      }, 1500);
      return () => clearTimeout(t);
    }

    const u = new SpeechSynthesisUtterance(text);
    u.lang = i18n.language === 'en' ? 'en-US' : i18n.language;
    u.rate = 1.0;
    u.pitch = 1.0;

    setSpeaking(true);

    u.onend = () => {
      setSpeaking(false);
      if (!autoPlayRef.current) return;
      // 一次模式末頁 → 停止；循環模式 → 回第一頁；其他 → 推進
      setIdx((prev) => {
        if (prev >= total - 1) {
          if (loopModeRef.current) return 0;
          setAutoPlay(false);
          return prev;
        }
        return prev + 1;
      });
    };
    u.onerror = () => { setSpeaking(false); };

    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);

    return () => {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    };
  }, [hostMode, autoPlay, slide?.id, total, i18n.language]);

  // hostMode 關閉時取消朗讀
  useEffect(() => {
    if (!hostMode && typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  }, [hostMode]);

  // 元件 unmount 時取消朗讀
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);


  // =====================================================================
  if (loading || permissionLoading) return <Box sx={{ p: 3 }}><LinearProgress /></Box>;
  if (!hasPermission(FUNC_CODE, 'read')) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('common.noPermission', '無權限存取此頁面')}</Typography>
    </Box>
  );
  if (!data || !slide) return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('opsCenter.present.noSlides')}</Typography>
      <IconButton onClick={() => navigate(`/ee_ops_centers/${code}`)}>
        <ArrowBackIcon /> <Typography sx={{ ml: 1 }}>{t('opsCenter.present.backToCenter')}</Typography>
      </IconButton>
    </Box>
  );

  return (
    <Box ref={containerRef} sx={{
      position: 'relative',
      bgcolor: '#0b1220',
      color: 'white',
      minHeight: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* 頂部 bar */}
      <Box sx={{
        px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: 'rgba(0,0,0,0.35)',
      }}>
        <IconButton size="small" sx={{ color: 'white' }}
                    onClick={() => setShowToc((s) => !s)}
                    title={t('opsCenter.present.tooltips.menuToc')}>
          <MenuIcon />
        </IconButton>
        <IconButton size="small" sx={{ color: 'white' }}
                    onClick={() => navigate(`/ee_ops_centers/${code}`)}
                    title={t('opsCenter.present.tooltips.close')}>
          <CloseIcon />
        </IconButton>
        <Typography variant="body2" sx={{ opacity: 0.9, flexGrow: 1 }}>
          {data.center.name}
        </Typography>

        {/* 分類過濾下拉（有 categories 才顯示） */}
        {allCategories.length > 0 && (
          <Select
            size="small"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(String(e.target.value))}
            variant="outlined"
            sx={{
              color: 'white', fontSize: 13, height: 28, maxWidth: 220,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
              '& .MuiSvgIcon-root': { color: 'white' },
            }}
            MenuProps={{ container: containerRef.current }}
            title={t('opsCenter.present.tooltips.categoryFilter')}
          >
            <MenuItem value="__all__">{t('opsCenter.present.filter.allItems')}</MenuItem>
            <MenuItem value="__nocat__">{t('opsCenter.present.filter.uncategorized')}</MenuItem>
            {allCategories.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        )}

        {/* Tag 多選過濾（有 tags 才顯示；多選取交集） */}
        {(data.tags?.length ?? 0) > 0 && (
          <Select
            multiple
            size="small"
            value={tagFilter}
            onChange={(e) => {
              const v = e.target.value;
              setTagFilter(typeof v === 'string' ? [] : (v as number[]));
            }}
            displayEmpty
            renderValue={(sel) => {
              const arr = sel as number[];
              if (arr.length === 0) return <span style={{ opacity: 0.7 }}>{t('opsCenter.present.filter.tagPlaceholder')}</span>;
              return (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {arr.map((id) => {
                    const t = tagById.get(id);
                    return t ? (
                      <Chip key={id} size="small" label={t.tag_name}
                            sx={{ bgcolor: t.color, color: '#fff',
                                  height: 18, fontSize: 11 }} />
                    ) : null;
                  })}
                </Box>
              );
            }}
            sx={{
              color: 'white', fontSize: 13, height: 28, maxWidth: 280,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
              '& .MuiSvgIcon-root': { color: 'white' },
            }}
            MenuProps={{ container: containerRef.current }}
            title={t('opsCenter.present.tooltips.tagFilter')}
          >
            {(data.tags ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <Chip size="small" label={t.tag_name}
                      sx={{ bgcolor: t.color, color: '#fff', mr: 1 }} />
                {t.tag_code}
              </MenuItem>
            ))}
          </Select>
        )}

        <Chip size="small" label={`${idx + 1} / ${total}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />

        {/* 主持人朗讀切換（模式設定） */}
        <IconButton size="small" sx={{ color: hostMode ? '#7dd3fc' : 'white' }}
                    onClick={() => setHostMode((h) => !h)}
                    title={hostMode
                      ? t('opsCenter.present.tooltips.hostOff')
                      : t('opsCenter.present.tooltips.hostOn')}>
          {hostMode ? <HostOnIcon /> : <HostOffIcon />}
        </IconButton>

        {/* 播放策略：循環 / 一次 切換 */}
        <IconButton size="small" sx={{ color: 'white' }}
                    onClick={() => setLoopMode((l) => !l)}
                    title={loopMode
                      ? t('opsCenter.present.tooltips.loopMode')
                      : t('opsCenter.present.tooltips.onceMode')}>
          {loopMode ? <LoopIcon /> : <OnceIcon />}
        </IconButton>

        {/* 播放 / 暫停（主控）+ 速度下拉 */}
        <IconButton size="small" sx={{ color: 'white' }}
                    onClick={() => setAutoPlay((p) => !p)}
                    title={autoPlay
                      ? t('opsCenter.present.tooltips.pause')
                      : t('opsCenter.present.tooltips.play')}>
          {autoPlay ? <PauseIcon /> : <PlayIcon />}
        </IconButton>
        <Select
          size="small"
          value={intervalSec}
          onChange={(e) => setIntervalSec(Number(e.target.value))}
          variant="outlined"
          sx={{
            color: 'white',
            fontSize: 13,
            height: 28,
            minWidth: 70,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
            '& .MuiSvgIcon-root': { color: 'white' },
          }}
          MenuProps={{ container: containerRef.current }}
          title={t('opsCenter.present.tooltips.intervalSelect')}
        >
          <MenuItem value={3}>{t('opsCenter.present.interval', { n: 3 })}</MenuItem>
          <MenuItem value={5}>{t('opsCenter.present.interval', { n: 5 })}</MenuItem>
          <MenuItem value={10}>{t('opsCenter.present.interval', { n: 10 })}</MenuItem>
          <MenuItem value={15}>{t('opsCenter.present.interval', { n: 15 })}</MenuItem>
          <MenuItem value={30}>{t('opsCenter.present.interval', { n: 30 })}</MenuItem>
        </Select>

        <IconButton size="small" sx={{ color: 'white' }}
                    onClick={() => setShowInfo((s) => !s)}
                    title={t('opsCenter.present.tooltips.info')}>
          {showInfo ? <InfoIcon /> : <InfoOutlinedIcon />}
        </IconButton>
        <IconButton size="small" sx={{ color: 'white' }} onClick={toggleFullscreen}
                    title={t('opsCenter.present.tooltips.fullscreen')}>
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Box>

      {/* 進度 bar */}
      <Box sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.08)' }}>
        <Box sx={{
          height: '100%',
          width: `${((idx + 1) / total) * 100}%`,
          bgcolor: '#7dd3fc',
          transition: 'width .2s',
        }} />
      </Box>

      {/* 主要展示區 */}
      <Box sx={{
        flexGrow: 1,
        display: 'grid',
        gridTemplateColumns: showInfo ? '1fr 400px' : '1fr',
        minHeight: 0,
      }}>
        {/* 左：原件 */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          p: 3,
        }}>
          {slide.preview_url ? (
            <AuthImage src={slide.preview_url} alt={slide.title}
                       style={{
                         maxWidth: '100%',
                         maxHeight: '100%',
                         objectFit: 'contain',
                         boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                         borderRadius: 4,
                       }} />
          ) : (
            <Paper sx={{
              p: 4, bgcolor: '#1e293b', color: 'white',
              maxWidth: 900, width: '100%', maxHeight: '80vh', overflow: 'auto',
            }}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
                {slide.title}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {slide.raw_text
                  || getI18nValue(slide.ai_summary, i18n.language)
                  || t('opsCenter.present.noContent')}
              </Typography>
            </Paper>
          )}

          {/* 主持人 Avatar — hostMode 開啟時顯示在右下角 */}
          {hostMode && (
            <Box sx={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              width: 88,
              height: 88,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: speaking ? '#7dd3fc' : 'rgba(125, 211, 252, 0.3)',
              color: 'white',
              transition: 'background-color .3s',
              zIndex: 5,
              ...(speaking && {
                animation: 'host-pulse 1.4s infinite',
              }),
              '@keyframes host-pulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(125, 211, 252, 0.7)' },
                '70%': { boxShadow: '0 0 0 18px rgba(125, 211, 252, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(125, 211, 252, 0)' },
              },
            }}>
              <HostOnIcon sx={{ fontSize: 44 }} />
            </Box>
          )}

          <IconButton
            onClick={prev} disabled={idx === 0}
            sx={{
              position: 'absolute', left: 16, top: '50%',
              transform: 'translateY(-50%)', color: 'white',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <NavBeforeIcon fontSize="large" />
          </IconButton>
          <IconButton
            onClick={next} disabled={idx === total - 1}
            sx={{
              position: 'absolute', right: 16, top: '50%',
              transform: 'translateY(-50%)', color: 'white',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
            }}
          >
            <NavNextIcon fontSize="large" />
          </IconButton>
        </Box>

        {/* 右：對照文字 */}
        {showInfo && (
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.04)',
            borderLeft: '1px solid rgba(255,255,255,0.1)',
            overflow: 'auto',
            p: 3,
          }}>
            <Typography variant="overline" sx={{ opacity: 0.6 }}>
              #{slide.id} · {slide.material_type}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 0.5 }}>
              {slide.title}
            </Typography>

            {(() => {
              const summary = getI18nValue(slide.ai_summary, i18n.language);
              return summary ? (
                <>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>{t('opsCenter.present.panel.aiSummary')}</Typography>
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {summary}
                  </Typography>
                  <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 2 }} />
                </>
              ) : null;
            })()}

            {slide.raw_text && (
              <>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>{t('opsCenter.present.panel.rawText')}</Typography>
                <Typography variant="body2"
                            sx={{
                              mb: 2, whiteSpace: 'pre-wrap',
                              fontSize: 13, lineHeight: 1.7,
                            }}>
                  {slide.raw_text}
                </Typography>
              </>
            )}

            {slide.ai_tags && Object.keys(slide.ai_tags).length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 2 }} />
                <Typography variant="caption" sx={{ opacity: 0.6 }}>{t('opsCenter.present.panel.aiTags')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {Object.entries(slide.ai_tags as Record<string, unknown>).map(([k, v]) => (
                    <Chip key={k} size="small" label={`${k}: ${v ?? '-'}`}
                          sx={{ bgcolor: 'rgba(125,211,252,0.15)', color: '#bae6fd' }} />
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* 底部快捷鍵 */}
      <Box sx={{
        px: 2, py: 0.5, bgcolor: 'rgba(0,0,0,0.3)',
        display: 'flex', gap: 3, fontSize: 12, opacity: 0.6,
      }}>
        <span>{t('opsCenter.present.shortcuts.navigate')}</span>
        <span>{t('opsCenter.present.shortcuts.next')}</span>
        <span>{t('opsCenter.present.shortcuts.firstLast')}</span>
        <span>{t('opsCenter.present.shortcuts.play')}</span>
        <span>{t('opsCenter.present.shortcuts.host')}</span>
        <span>{t('opsCenter.present.shortcuts.fullscreen')}</span>
        <span>{t('opsCenter.present.shortcuts.info')}</span>
        <span>{t('opsCenter.present.shortcuts.toc')}</span>
        <span>{t('opsCenter.present.shortcuts.exit')}</span>
      </Box>

      {/* 目錄 Drawer — 左側滑入的跳轉選單
          container={containerRef.current} 讓 Drawer 渲染在展示間容器內，
          全螢幕模式下才看得到；否則 MUI 預設 portal 到 body 會跑出全螢幕範圍 */}
      <Drawer
        anchor="left"
        open={showToc}
        onClose={() => setShowToc(false)}
        hideBackdrop={false}
        container={containerRef.current}
        ModalProps={{
          // 全螢幕下 Backdrop / Modal 也要 portal 到容器內
          container: containerRef.current,
          // 關掉 fixed positioning，讓元素以容器為邊界而不是 viewport
          disablePortal: false,
        }}
        PaperProps={{
          sx: {
            width: TOC_WIDTH,
            bgcolor: '#0b1220',
            color: 'white',
            borderRight: '1px solid rgba(255,255,255,0.1)',
            position: 'absolute',  // 相對於 containerRef（它是 position: relative）
          },
        }}
        sx={{
          // Drawer 根元素也要 absolute 才不會 escape 全螢幕容器
          position: 'absolute',
          '& .MuiBackdrop-root': { position: 'absolute' },
        }}
      >
        <Box sx={{
          p: 2, display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {t('opsCenter.present.tocTitle')}
          </Typography>
          <Chip size="small" label={t('opsCenter.present.tocCount', { count: total })}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
        </Box>
        <List sx={{ p: 0 }}>
          {filteredSlides.map((s, i) => {
            const selected = i === idx;
            return (
              <ListItemButton
                key={s.id}
                selected={selected}
                onClick={() => jumpTo(i)}
                sx={{
                  py: 1.25,
                  px: 2,
                  borderLeft: selected ? '3px solid #7dd3fc' : '3px solid transparent',
                  bgcolor: selected ? 'rgba(125,211,252,0.12)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                {/* 縮圖（若有）*/}
                {s.preview_url && (
                  <Box sx={{
                    width: 48, height: 36, mr: 1.5,
                    flexShrink: 0,
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    bgcolor: '#000',
                  }}>
                    <AuthImage src={s.preview_url} alt=""
                               style={{
                                 width: '100%', height: '100%', objectFit: 'cover',
                               }} />
                  </Box>
                )}
                <ListItemText
                  primary={
                    <Typography variant="body2"
                                sx={{
                                  fontWeight: selected ? 600 : 400,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  fontSize: 13,
                                }}>
                      {i + 1}. {s.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>
    </Box>
  );
};

export default OpsCenterPresentPage;
